import { HttpInterceptorFn } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
    APP_INITIALIZER,
    EnvironmentProviders,
    Provider,
} from '@angular/core';
import { acceptLanguageInterceptor } from 'app/core/interceptors/accept-language.interceptor';
import { Router } from '@angular/router';
import { environment } from 'environments/environment';
import { AuthService } from 'app/core/services/auth.service';
import { AuthorizationService } from 'app/core/services/authorization.service';
import { DeviceIdService } from 'app/core/services/device-id.service';
import { TenantContextService } from 'app/core/services/tenant-context.service';
import { ToastService } from 'app/core/services/toast.service';
import { readGrantSession } from 'app/core/services/auth/grant-session';
import { apiErrorInterceptor } from 'app/core/interceptors/api-error.interceptor';
import { provideNeraI18n } from 'app/core/providers/i18n.provider';
import { csrfInterceptor } from 'app/core/interceptors/csrf.interceptor';
import { grantSessionInterceptor } from 'app/core/interceptors/grant-session.interceptor';
import { dedupeGetInterceptor } from 'app/core/interceptors/dedupe-get.interceptor';
import { orgMismatchInterceptor } from 'app/core/interceptors/org-mismatch.interceptor';
import { quotaInterceptor } from 'app/core/interceptors/quota.interceptor';
import {
    API_BASE_URL,
    NERA_API_ERROR_CONFIG,
    NERA_AUTH_SERVICE,
    NERA_DEVICE_ID_SERVICE,
    NERA_NAVIGATOR,
    NERA_TOAST_SERVICE,
    neraAuthInterceptor,
    neraLanguageInterceptor,
} from 'nera-common/angular';

/**
 * URLs whose GET 403 means "this whole page is forbidden", so the error
 * interceptor should redirect rather than just toast. `NERA_API_ERROR_CONFIG`
 * defaults to an empty list, so this must be supplied explicitly.
 */
const FORBIDDEN_PAGE_PATTERNS = [
    // PFL-1671: matching is now segment-boundary in nera-common, so
    // '/api/v1/users' matches only the users list itself — the widget call
    // '/api/v1/users/me/quota' (fires for every role on /overview) no longer
    // dumps the whole shell to /errors/forbidden on a 403.
    '/api/v1/users',
    '/api/v1/invitations',
    '/api/v1/organizations/my-studio',
    '/api/v1/licenses',
    // PFL-1683: '/api/v1/quota' used to be here, but it's a substring match —
    // it also caught '/api/v1/quotas/usage/{orgId}', the app-wide quota-banner
    // widget call (classy.component.ts), whose own local error handler
    // already swallows failures. A 403 there killed the whole route instead
    // of just leaving the widget empty. No current endpoint under
    // '/api/v1/quota*' is a genuine full-page surface (usage/me and
    // usage/{orgId} are both widget calls; quota/plans/public is anonymous).
];

/**
 * On app bootstrap, restore BFF session from __nera_at httpOnly cookie before
 * any route guard evaluates (T10: PFL-1281).
 */
export function initBffSession(
    authService: AuthService,
    authorization: AuthorizationService,
    tenantContext: Pick<TenantContextService, 'moveToActingOrgHost'>,
    location: Pick<Location, 'pathname' | 'search'> = globalThis.location
): () => Promise<void> {
    return async () => {
        // An album-grant visitor (`/p/{code}`, or a public gallery with a stored
        // grant) has no IAM account and no BFF cookie: `GET /auth/session/me` and
        // the follow-up `POST /auth/session/refresh` can only 401, and did so on
        // every load of the client's page. The grant entry mints its own bearer.
        // A photographer opening `/public/…` without a grant still restores.
        const path = location.pathname;
        if (path.startsWith('/p/') || (path.startsWith('/public') && readGrantSession())) {
            return;
        }
        // A failed IAM hand-off: the BFF callback lands every denied authorize on
        // `/sign-in?error=<code>`, and that page owns the recovery (sign out
        // whatever session the BFF still holds, re-hand off once, then stop).
        // Restoring the leftover session here instead let NoAuthGuard bounce the
        // page away, and let moveToActingOrgHost re-hand off the same denied
        // session on every load — /sign-in?error= → authorize → /sign-in?error=
        // until /auth/session/login answered 429.
        if (path === '/sign-in' && new URLSearchParams(location.search).has('error')) {
            return;
        }
        const restored = await authService.restoreSessionFromBff();
        if (!restored) {
            return;
        }
        // `session/me` roles are flat token claims — they can carry a role the
        // user holds in ANOTHER org (e.g. a Studio-org OWNER showing as ADMIN
        // on the root host). The org-scoped MyPermissions (source of truth) rides
        // the same response (`?include=permissions`, FSH-B2): apply it before
        // guards evaluate so roles/permissions/isAdmin reflect the acting org from
        // first render. Fall back to GET me/permissions only when an older
        // identity-service omits it. On failure AuthGuard retries and fails closed.
        if (restored.permissions) {
            authorization.applyPermissions(restored.permissions);
        } else {
            try {
                await firstValueFrom(authorization.hydratePermissions());
            } catch (err) {
                console.warn(
                    '[auth] me/permissions hydration failed; AuthGuard will retry',
                    err
                );
            }
        }
        // One studio, one host: a studio session restored on the root host
        // belongs on the studio's own host. Hold bootstrap while the browser
        // navigates there, so the root shell never renders for it.
        if (await tenantContext.moveToActingOrgHost()) {
            await new Promise<never>(() => undefined);
        }
    };
}

/**
 * HTTP interceptors auth needs registered on the app's single
 * `provideHttpClient()` call (PFL-1679: previously registered via a
 * dedicated `provideHttpClient()` here, which — combined with the other two
 * `provideHttpClient()` calls in app.config.ts and fuse.provider.ts — fired
 * every interceptor multiple times per request).
 *
 * Order matters: auth first (it owns the 401 refresh-retry), the API error
 * interceptor last (it passes 401 through untouched). Callers must merge
 * this into their own `withInterceptors([...])` array instead of calling
 * `provideHttpClient()` again here.
 */
export const authInterceptors: HttpInterceptorFn[] = [
    neraAuthInterceptor,
    // Directly INSIDE neraAuthInterceptor: it must see a 401 before the auth
    // interceptor's refresh-or-redirect-to-IAM branch does, so an album-grant
    // session (credential = `/p/{code}`, no IAM account) can re-mint its
    // bearer and retry instead of being bounced to the login page.
    grantSessionInterceptor,
    // AFTER neraAuthInterceptor on purpose: it must observe the Authorization
    // header that interceptor attaches, because the server skips CSRF
    // validation for bearer callers (PFL-2079).
    csrfInterceptor,
    // AFTER the header-adding interceptors so the dedupe key sees the final
    // Authorization header; BEFORE language, which is a pure header add.
    dedupeGetInterceptor,
    neraLanguageInterceptor,
    acceptLanguageInterceptor,
    // neraApiErrorInterceptor, wrapped (kb#2220): static assets skip it and
    // its rejection is rethrown as a real `Error` (NeraApiError).
    apiErrorInterceptor,
    // AFTER neraApiErrorInterceptor, same reasoning as orgMismatchInterceptor below:
    // the later entry sees the error first, which is the only place a quota 429 can
    // pre-empt the generic "Too many requests" toast (D2). Unlike orgMismatch it
    // re-throws, so callers still get their error handler.
    quotaInterceptor,
    // LAST on purpose. Angular runs interceptors outside-in on the way down and
    // inside-out on the way back, so the final entry sees an error FIRST — the only
    // position from which neraApiErrorInterceptor's toast can be pre-empted. On a
    // mismatched host every request 403s, so without this the user gets one
    // identical "Access denied" toast per in-flight call and no explanation.
    orgMismatchInterceptor,
];

export const provideAuth = (): Array<Provider | EnvironmentProviders> => {
    return [
        // Provide the API base URL token used by RestApiService and the auth interceptor
        { provide: API_BASE_URL, useValue: environment.api.server },

        // Provide token aliases for the shared interceptors and guards
        { provide: NERA_AUTH_SERVICE, useExisting: AuthService },
        { provide: NERA_DEVICE_ID_SERVICE, useExisting: DeviceIdService },
        { provide: NERA_TOAST_SERVICE, useExisting: ToastService },

        // Adapters for the two ports that keep @jsverse/transloco and
        // @angular/router out of nera-common's dependencies.
        ...provideNeraI18n(),
        {
            provide: NERA_NAVIGATOR,
            // Only nera-common's page-level 403 redirect navigates through this. replaceUrl
            // swaps the failing page for the error page in the browser history, so the browser's
            // back button can't return to a page that 403s again (see SafeBackService).
            useFactory: (router: Router) => ({
                navigateByUrl: (url: string) =>
                    router.navigateByUrl(url, { replaceUrl: true }),
            }),
            deps: [Router],
        },
        {
            provide: NERA_API_ERROR_CONFIG,
            // Kanban #63: nera-common's default forbiddenRedirectUrl
            // ('/errors/forbidden') is the bare, shell-less page — override it
            // to the in-shell one (see in-shell-forbidden.component.ts) so a
            // page-level 403 on GET /users, /invitations, /organizations/
            // my-studio or /licenses doesn't drop the user out of the app
            // shell, same fix as RouteDataAuthorizationGuard's redirect target.
            useValue: {
                forbiddenPagePatterns: FORBIDDEN_PAGE_PATTERNS,
                forbiddenRedirectUrl: '/forbidden',
            },
        },

        {
            provide: APP_INITIALIZER,
            useFactory: (
                authService: AuthService,
                authorization: AuthorizationService,
                tenantContext: TenantContextService
            ) => initBffSession(authService, authorization, tenantContext),
            deps: [AuthService, AuthorizationService, TenantContextService],
            multi: true,
        },
    ];
};
